// Shared wire types. snake_case matches the API's JSON verbatim (see
// v5/apps/api/src/{player,runs,live,strains}-routes.ts) — no case translation
// layer, same convention the contract tests use.

export type PlayerTokenPayload = { pid: number | null; adm: boolean; org: number | null };

export type Player = {
  id: number;
  name: string;
  active: number;
  created_at: number;
  is_admin: number;
  role: "worker" | "manager" | "admin";
  ha_linked: number;
  has_pin: number;
};

export type AuthedPlayer = { id: number; name: string };

export type Strain = { id: number; name: string; last_used_at: number; use_count: number };

export type Job = {
  id: number;
  name: string;
  strain_id: number | null;
  strain_name?: string | null;
  trim_type: string | null;
  nug_size: string | null;
  target_grams: number;
  // "closed" is a manager-initiated PATCH /api/jobs/:id status distinct from
  // "done" (auto-set once the target weight lands) — runs-routes.ts accepts
  // both, this type just didn't list the third one yet.
  status: "open" | "done" | "closed";
  done_grams: number;
  remaining_grams: number;
  pct: number;
  eta_minutes: number | null;
  rate_gpm: number | null;
  runs: number;
  crew: number;
  trim_minutes: number;
  created_at: number;
  completed_at: number | null;
  just_completed?: boolean;
};

export type LiveSession = {
  bag?: { id: string; label: string; batchId: string; strainId: number; strainName: string };
  id: number;
  client_uuid: string;
  player_id: number;
  strain_id: number | null;
  mode: string;
  status: string;
  started_at: number;
  paused: number;
  paused_ms: number;
  start_grams: number | null;
  device_id: string | null;
  last_seen: number;
  cancelled_by: string | null;
  harvest_id: number | null;
  stop_pending_at: number | null;
  player_name: string | null;
  player_avatar_at?: number | null;
  strain_name: string | null;
  harvest_name: string | null;
  elapsed_active_ms: number;
  resume_deadline_ms?: number;
  resume_left_ms?: number;
};

export type RunRow = {
  id: number;
  client_uuid: string;
  player_id: number;
  strain_id: number | null;
  mode: string;
  started_at: number;
  stopped_at: number | null;
  paused_ms: number;
  active_ms: number;
  start_grams: number | null;
  end_grams: number | null;
  qa_score: number | null;
  qa_notes: string | null;
  gpm: number | null;
  yield_pct: number | null;
  device_id: string | null;
  harvest_id: number | null;
  job_id: number | null;
  trim_type: string | null;
  nug_size: string | null;
  // Present on boards-routes.ts's runJson() / GET /api/runs — was missing
  // from this type until M5b's dashboard needed to show merge/tombstone
  // status on a run row.
  merged_from: string | null;
  superseded_by: number | null;
  tombstone: number;
  revision: number;
  created_at: number;
  player_name: string | null;
  strain_name: string | null;
  harvest_name: string | null;
};

export const TRIM_TYPES = ["wet", "dry", "bag", "dry_batch", "wet_batch"] as const;
export const NUG_SIZES = ["small", "medium", "large", "mixed"] as const;
export type TrimType = (typeof TRIM_TYPES)[number];
export type NugSize = (typeof NUG_SIZES)[number];

// ── M5b: manager surfaces (board / dashboard / console) ─────────────────────
// Wire types for boards-routes.ts / runs-routes.ts's leaderboard+jobs /
// tokens-routes.ts / the new GET /api/console (org-routes.ts).

export type Harvest = {
  id: number;
  name: string;
  started_at: number;
  active: number;
  notes: string | null;
  created_at: number;
  runs: number;
  total_grams: number;
};

export type DashboardPerPlayer = {
  id: number; name: string; runs: number; total_grams: number; total_active_ms: number;
  avg_gpm: number; avg_qa: number; last_run: number | null;
};
export type DashboardPerStrain = {
  id: number; name: string; runs: number; total_start: number; total_end: number;
  avg_yield: number; avg_qa: number;
};
export type DashboardPerHarvest = {
  id: number; name: string; active: number; started_at: number; notes: string | null;
  runs: number; total_grams: number; total_active_ms: number; avg_yield: number; avg_qa: number;
};
export type DashboardDaily = { day: string; runs: number; grams: number };
export type DashboardTotals = {
  runs: number; total_grams: number; total_ms: number; avg_gpm: number; avg_qa: number;
};
export type DashboardData = {
  today: RunRow[];
  recent: RunRow[];
  per_player: DashboardPerPlayer[];
  per_strain: DashboardPerStrain[];
  per_harvest: DashboardPerHarvest[];
  flags: RunRow[];
  daily: DashboardDaily[];
  totals: DashboardTotals;
  ai_on: boolean;
};

export type LeaderboardRow = {
  id: number; name: string; runs: number; total_grams: number; total_active_ms: number;
  avg_gpm: number; adj_gpm: number; best_gpm: number; avg_qa: number; last_run: number; badges: number;
};
export type LeaderboardHarvestLite = { id: number; name: string; active: number };
export type LeaderboardResponse = {
  board: LeaderboardRow[];
  harvests: LeaderboardHarvestLite[];
  harvest_id: number | null;
  days: number | null;
};

export type ApiTokenRow = {
  id: number;
  name: string;
  scope: "read" | "write";
  created_at: number;
  last_used_at: number | null;
  revoked: number;
};

export type ConsoleContext = {
  org: { id: number; name: string; plan: string; paid_seats: number };
  locked: boolean;
  days_left: number | null;
  seat_cap: number | null;
  join_url: string | null;
  stripe_enabled: boolean;
  has_stripe_customer: boolean;
  deployment_mode?: "saas" | "self_hosted";
};

export type BillingResponse = {
  enabled: boolean;
  environment: "sandbox" | "live";
  currency: string;
  unit_amount: number;
  /** Regular price; differs from unit_amount when the Founding 100 price applies. */
  regular_unit_amount?: number;
  founding?: boolean;
  interval: "month";
  trial_ends_at: number | null;
  paid_through_at: number | null;
  active_crew: number;
  seats: number;
  monthly_total: number;
  plan: string;
  status: string;
  locked: boolean;
  can_checkout: boolean;
  can_cancel: boolean;
  approval_url: string | null;
  subscription_id: string | null;
  can_revise: boolean;
  pending_seats: number | null;
  revision_approval_url: string | null;
};

/** An org's configured work type. The hardcoded TRIM_TYPES above remain as
 * the built-in slugs and as an offline fallback for the chip list; the live
 * list comes from GET /api/work-types so a room sees its own styles. */
export type WorkType = {
  id: number;
  slug: string;
  label: string;
  speed_factor: number;
  is_machine: boolean;
  builtin: boolean;
  active: boolean;
  sort_order: number;
};
